import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import TopBar from './components/TopBar';
import { discoverService, DiscoverSettings } from './services/DiscoverService';
import Footer from './components/Footer';
import HomeView from './components/home/HomeView';
import SearchAssistant from './components/SearchAssistant';
import BrandCatalog from './components/BrandCatalog';
import ProductDetailView from './components/ProductDetailView';
import InquiryDrawer from './components/InquiryDrawer';
import LegalModal from './components/LegalModal';
import { useRecentlyViewed } from './hooks/useRecentlyViewed';
import { BRANCHES } from './config/siteConfig';
import { Branch, FilterState, Product } from './types';
import { AnimatePresence } from 'framer-motion';
import { productService } from './services/ProductService';
import { analytics } from './services/analytics';
import { useSEO } from './hooks/useSEO';
import WhatsAppFAB from './components/WhatsAppFAB';
import { isAdminSubdomain } from './utils/domain';
import AdminLayout from './admin/AdminLayout';
import { ToastProvider } from './context/ToastContext';
import { ReviewFormModal } from './components/reviews/ReviewFormModal';
import { AllReviewsView } from './components/reviews/AllReviewsView';
import { SocialView } from './components/social/SocialView';
import NotFoundPage from './components/NotFoundPage';
import MaintenancePage from './components/MaintenancePage';
import { supabase } from './lib/supabase';

const App: React.FC = () => {
    // State
    const [currentBranch, setCurrentBranch] = useState<Branch>(BRANCHES[0]);
    const [isSearchAssistantOpen, setIsSearchAssistantOpen] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
    const [selectedModel, setSelectedModel] = useState<string | null>(null);
    const [brandProducts, setBrandProducts] = useState<Product[]>([]);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [discoverSettings, setDiscoverSettings] = useState<DiscoverSettings | null>(null);
    const [isNotFound, setIsNotFound] = useState(false);
    const [hasAdminSession, setHasAdminSession] = useState(false);

    // Architect: Ensure page starts at the top on reload
    useEffect(() => {
        window.history.scrollRestoration = 'manual';
        window.scrollTo(0, 0);
        analytics.init();
        discoverService.getSettings().then(setDiscoverSettings);

        supabase.auth.getSession().then(({ data: { session } }) => {
            setHasAdminSession(!!session);
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setHasAdminSession(!!session);
        });

        return () => subscription.unsubscribe();
    }, []);

    // Search State
    const [searchQuery, setSearchQuery] = useState<string | null>(null);
    const [searchResults, setSearchResults] = useState<Product[]>([]);

    // UX/UI Phase 3 State
    const { recentlyViewed, addToRecentlyViewed } = useRecentlyViewed();
    const [viewingProduct, setViewingProduct] = useState<Product | null>(null);

    // Legal Modal State
    const [legalModalOpen, setLegalModalOpen] = useState(false);
    const [legalModalType, setLegalModalType] = useState<'privacy' | 'terms' | null>(null);

    // Review Modal & Page State
    const [reviewTokenInfo, setReviewTokenInfo] = useState<{token: string} | null>(null);
    const [showReviewsPage, setShowReviewsPage] = useState(false);
    const [showSocialPage, setShowSocialPage] = useState(false);

    // Initial Filters for Catalog
    const [catalogFilters, setCatalogFilters] = useState<{
        magsafe?: boolean;
        forged?: boolean;
        carbon?: boolean;
    }>({});

    // Dynamic SEO
    useSEO({
        title: isNotFound ? 'Página No Encontrada' : undefined,
        brand: isNotFound ? null : selectedBrand,
        searchQuery: isNotFound ? null : searchQuery,
        viewingProduct: isNotFound ? null : viewingProduct
    });

    const handleOpenLegal = (type: 'privacy' | 'terms') => {
        setLegalModalType(type);
        setLegalModalOpen(true);
    };

    // Realtime Ref
    const viewingProductRef = React.useRef(viewingProduct);
    useEffect(() => { viewingProductRef.current = viewingProduct; }, [viewingProduct]);

    const handleProductSelect = (product: Product) => {
        setViewingProduct(product);
        addToRecentlyViewed(product);
        analytics.sendPageView(`/product/${product.id}`, `Product: ${product.name}`);
        analytics.sendEvent('Product', 'View', product.name);

        // Persist to URL
        const url = new URL(window.location.href);
        url.searchParams.set('product', product.id);
        window.history.pushState({}, '', url);
        window.scrollTo(0, 0);
    };

    const handleSearch = (filters: FilterState) => {
        if (filters.make) {
            setSelectedBrand(filters.make);
            setSelectedModel(filters.model || null);
        }
    };

    // URL slug helpers
    const brandToSlug = (brand: string): string => {
        return brand.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    };

    const slugToBrand = (slug: string): string => {
        const normalized = slug.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        if (normalized === 'catalogo') return 'Catálogo';
        if (normalized === 'accesorios') return 'Accesorios';
        if (normalized === 'apple') return 'Apple';
        if (normalized === 'samsung') return 'Samsung';
        return slug;
    };

    // URL State Sync
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const brandParam = params.get('brand');
        const productParam = params.get('product');
        const reviewParam = params.get('review');
        const reviewTokenParam = params.get('token');
        const viewParam = params.get('view');
        
        if (reviewParam === 'true' && reviewTokenParam) {
            setReviewTokenInfo({ token: reviewTokenParam });
        }

        if (viewParam === 'social') {
            setShowSocialPage(true);
        }

        if (productParam) {
            productService.getProductById(productParam).then(product => {
                if (product) {
                    setViewingProduct(product);
                    addToRecentlyViewed(product);
                } else {
                    setIsNotFound(true);
                }
            }).catch(() => {
                setIsNotFound(true);
            });
        }
        
        if (brandParam) {
            setSelectedBrand(slugToBrand(brandParam));
        }
    }, []);

    const handleGoHome = () => {
        setIsNotFound(false);
        setViewingProduct(null);
        setSelectedBrand(null);
        setSelectedModel(null);
        setSearchQuery(null);
        setShowSocialPage(false);
        setShowReviewsPage(false);
        setCatalogFilters({});
        const url = new URL(window.location.origin + window.location.pathname);
        window.history.pushState({}, '', url);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSelectBrand = (brand: string) => {
        setIsNotFound(false);
        setShowSocialPage(false);
        setShowReviewsPage(false);
        setViewingProduct(null);
        setSelectedBrand(brand);
        setSelectedModel(null);
        setSearchQuery(null);
        // Clear filters if we are selecting a specific brand directly (not from material section)
        if (brand !== 'Catálogo') {
            setCatalogFilters({});
        }
        window.scrollTo(0, 0);

        // Persist to URL with clean slug
        const url = new URL(window.location.href);
        url.searchParams.set('brand', brandToSlug(brand));
        url.searchParams.delete('view');
        url.searchParams.delete('product');
        window.history.pushState({}, '', url);
    };

    const handleMaterialSelect = (material: 'carbon' | 'forged') => {
        setIsNotFound(false);
        setCatalogFilters({
            carbon: material === 'carbon',
            forged: material === 'forged'
        });
        handleSelectBrand('Catálogo');
    };

    const handleLogoClick = () => {
        setIsNotFound(false);
        setSelectedBrand(null);
        setSelectedModel(null);
        setSearchQuery(null);
        setViewingProduct(null);
        setShowSocialPage(false);
        setShowReviewsPage(false);
        setCatalogFilters({});
        window.scrollTo(0, 0);

        // Clear URL
        const url = new URL(window.location.href);
        url.searchParams.delete('brand');
        url.searchParams.delete('product');
        url.searchParams.delete('view');
        window.history.pushState({}, '', url);
    };

    const handleBackToBrands = () => {
        setSelectedBrand(null);
        setSelectedModel(null);

        // Clear URL
        const url = new URL(window.location.href);
        url.searchParams.delete('brand');
        window.history.pushState({}, '', url);
        // Scroll to catalog section after state updates - increased timeout
        setTimeout(() => {
            const element = document.getElementById('catalog-section');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                // Retry if element not found
                setTimeout(() => {
                    const retryElement = document.getElementById('catalog-section');
                    if (retryElement) {
                        retryElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 200);
            }
        }, 300);
    };

    const handleBackFromSearch = () => {
        setSearchQuery(null);
        // Scroll to catalog section after state updates - increased timeout
        setTimeout(() => {
            const element = document.getElementById('catalog-section');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                // Retry if element not found
                setTimeout(() => {
                    const retryElement = document.getElementById('catalog-section');
                    if (retryElement) {
                        retryElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 200);
            }
        }, 300);
    };

    const handleKeywordSearch = (query: string) => {
        if (!query.trim()) return;

        setIsLoadingProducts(true);
        setSearchQuery(query);
        setSelectedBrand(null);

        productService.searchProducts(query)
            .then(setSearchResults)
            .catch(console.error)
            .finally(() => setIsLoadingProducts(false));
    };

    useEffect(() => {
        if (selectedBrand) {
            analytics.sendPageView(`/brand/${selectedBrand}`, `Brand: ${selectedBrand}`);
            setIsLoadingProducts(true);
            let fetchAction;
            if (selectedBrand === 'Accesorios') {
                fetchAction = productService.getProductsByCategory('Accesorios');
            } else if (selectedBrand === 'Catálogo') {
                fetchAction = productService.getAllProducts().then(products => products.filter(p => p.is_active));
            } else {
                fetchAction = productService.getProductsByBrand(selectedBrand);
            }

            fetchAction
                .then(setBrandProducts)
                .catch(console.error)
                .finally(() => setIsLoadingProducts(false));
        } else {
            analytics.sendPageView('/', 'Home');
        }
    }, [selectedBrand]);

    // Global Realtime Subscription
    useEffect(() => {
        // Architect: If we are in Admin mode, we don't need the public product subscription
        // as AdminLayout/ProductsPage handle their own specialized admin subscriptions.
        if (isAdminSubdomain()) return;

        let isMounted = true;
        const unsubscribe = productService.subscribeToProducts((updatedProduct, eventType) => {
            if (!isMounted) return;
            if ((import.meta as any).env?.DEV) console.log(`[Realtime] ${eventType} received:`, updatedProduct);

            if (eventType === 'UPDATE') {
                // 1. Surgical update for Catalog List
                // We receive a raw partial update from Supabase, so we merge explicitly to avoid wiping arrays
                setBrandProducts(prev => prev.map(p => {
                    if (p.id === updatedProduct.id) {
                        return {
                            ...p,
                            is_magsafe: updatedProduct.is_magsafe ?? p.is_magsafe,
                            is_premium: updatedProduct.is_premium ?? p.is_premium,
                            is_last_units: updatedProduct.is_last_units ?? p.is_last_units,
                            is_active: updatedProduct.is_active ?? p.is_active,
                            price: updatedProduct.price ?? p.price,
                            name: updatedProduct.name ?? p.name,
                            base_sku: updatedProduct.base_sku ?? p.base_sku
                        };
                    }
                    return p;
                }));

                // 2. Surgical update for Search Results
                setSearchResults(prev => prev.map(p => {
                    if (p.id === updatedProduct.id) {
                        return {
                            ...p,
                            is_magsafe: updatedProduct.is_magsafe ?? p.is_magsafe,
                            is_premium: updatedProduct.is_premium ?? p.is_premium,
                            is_last_units: updatedProduct.is_last_units ?? p.is_last_units,
                            is_active: updatedProduct.is_active ?? p.is_active,
                            price: updatedProduct.price ?? p.price,
                            name: updatedProduct.name ?? p.name,
                            base_sku: updatedProduct.base_sku ?? p.base_sku
                        };
                    }
                    return p;
                }));

                // 3. Refresh Modal if viewing the same product
                if (viewingProductRef.current && updatedProduct.id === viewingProductRef.current.id) {
                    if ((import.meta as any).env?.DEV) console.log('[Realtime] Updating active modal product...');
                    setViewingProduct(prev => prev ? {
                        ...prev,
                        is_magsafe: updatedProduct.is_magsafe ?? prev.is_magsafe,
                        is_premium: updatedProduct.is_premium ?? prev.is_premium,
                        is_last_units: updatedProduct.is_last_units ?? prev.is_last_units,
                        is_active: updatedProduct.is_active ?? prev.is_active,
                        price: updatedProduct.price ?? prev.price,
                        name: updatedProduct.name ?? prev.name,
                        base_sku: updatedProduct.base_sku ?? prev.base_sku
                    } : null);
                }
            } else if (eventType === 'DELETE') {
                setBrandProducts(prev => prev.filter(p => p.id !== updatedProduct.id));
                setSearchResults(prev => prev.filter(p => p.id !== updatedProduct.id));
                if (viewingProductRef.current && updatedProduct.id === viewingProductRef.current.id) {
                    setViewingProduct(null);
                }
            } else if (eventType === 'INSERT') {
                // For inserts, let's just trigger a brand-refresh if we are in a brand view
                if (updatedProduct.make) {
                    const params = new URLSearchParams(window.location.search);
                    const currentBrand = params.get('brand');
                    if (currentBrand === updatedProduct.make) {
                        productService.getProductsByBrand(currentBrand).then(res => {
                            if (isMounted) setBrandProducts(res);
                        });
                    }
                }
            }
        }, 'is_active=eq.true');

        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, []); // Empty dependency array = persistent global listener
    // Actually, making it truly global without dependencies:

    const handleCategorySelect = (category: string, model?: string) => {
        setShowSocialPage(false);
        setShowReviewsPage(false);
        setViewingProduct(null);

        if (model) {
            setSearchQuery(model);
            productService.searchProducts(model).then(setSearchResults);
            return;
        }

        if (category === 'apple' || category === 'samsung') {
            handleSelectBrand(category.charAt(0).toUpperCase() + category.slice(1));
        } else if (category === 'accesorios') {
            handleSelectBrand('Accesorios');
        } else if (category === 'catalogo') {
            handleSelectBrand('Catálogo');
        } else if (category === 'social') {
            setShowSocialPage(true);
            setSelectedBrand(null);
            setSearchQuery(null);

            // Persist to URL
            const url = new URL(window.location.href);
            url.searchParams.delete('brand');
            url.searchParams.delete('product');
            url.searchParams.set('view', 'social');
            window.history.pushState({}, '', url);

            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (category === 'descubrir') {
            setSelectedBrand(null);
            setSearchQuery(null);

            // Clear URL
            const url = new URL(window.location.href);
            url.searchParams.delete('brand');
            url.searchParams.delete('product');
            url.searchParams.delete('view');
            window.history.pushState({}, '', url);

            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Architect: Admin Subdomain Routing
    if (isAdminSubdomain()) {
        return (
            <AdminLayout>
                <div>{/* El contenido del dashboard se maneja dentro de AdminLayout por ahora */}</div>
            </AdminLayout>
        );
    }

    // Architect: Maintenance Mode Guard (Allows bypass for active admin session)
    if (discoverSettings?.is_maintenance_mode && !hasAdminSession) {
        return (
            <MaintenancePage
                onOpenAdmin={() => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('mode', 'admin');
                    window.location.href = url.toString();
                }}
            />
        );
    }

    return (
        <div className="min-h-screen bg-black text-white font-sans selection:bg-white selection:text-black flex flex-col">
            <TopBar />
            <Header
                currentBranch={currentBranch}
                onBranchChange={setCurrentBranch}
                onLogoClick={handleLogoClick}
                onCategorySelect={handleCategorySelect}
            />

            <main className="flex-grow">
                <AnimatePresence mode="wait">
                    {isNotFound ? (
                        // 404 NOT FOUND VIEW
                        <NotFoundPage
                            key="not-found"
                            onGoHome={handleGoHome}
                        />
                    ) : viewingProduct ? (
                        // PRODUCT DETAIL VIEW
                        <ProductDetailView
                            key={`product-${viewingProduct.id}`}
                            product={viewingProduct}
                            currentBranch={currentBranch}
                            onBack={() => {
                                setViewingProduct(null);
                                const url = new URL(window.location.href);
                                url.searchParams.delete('product');
                                window.history.pushState({}, '', url);
                            }}
                        />
                    ) : showReviewsPage ? (
                        <AllReviewsView 
                            key="reviews-page" 
                            onClose={() => setShowReviewsPage(false)} 
                        />
                    ) : showSocialPage ? (
                        <SocialView 
                            key="social-page" 
                            onClose={() => {
                                setShowSocialPage(false);
                                const url = new URL(window.location.href);
                                url.searchParams.delete('view');
                                window.history.pushState({}, '', url);
                            }} 
                        />
                    ) : !selectedBrand && !searchQuery ? (
                        // HOME VIEW
                        <HomeView
                            key="home"
                            discoverSettings={discoverSettings}
                            currentBranch={currentBranch}
                            recentlyViewed={recentlyViewed}
                            onSelectMaterial={handleMaterialSelect}
                            onSelectBrand={handleSelectBrand}
                            onProductSelect={handleProductSelect}
                            onViewAllReviews={() => {
                                setShowReviewsPage(true);
                                window.scrollTo(0, 0);
                            }}
                            onOpenSearchAssistant={() => setIsSearchAssistantOpen(true)}
                            onKeywordSearch={handleKeywordSearch}
                        />
                    ) : selectedBrand ? (
                        // BRAND CATALOG VIEW
                        <BrandCatalog
                            key={`catalog-${selectedBrand}-${JSON.stringify(catalogFilters)}`}
                            brandName={selectedBrand}
                            products={brandProducts}
                            currentBranch={currentBranch}
                            onBack={handleBackToBrands}
                            isLoading={isLoadingProducts}
                            initialModel={selectedModel}
                            initialFilters={catalogFilters}
                            onProductSelect={handleProductSelect}
                        />
                    ) : (
                        // SEARCH RESULTS VIEW
                        <BrandCatalog
                            key={`search-${searchQuery}`}
                            brandName={searchQuery || "Búsqueda"}
                            products={searchResults}
                            currentBranch={currentBranch}
                            onBack={handleBackFromSearch}
                            isLoading={isLoadingProducts}
                            onProductSelect={handleProductSelect}
                            onSearch={handleKeywordSearch}
                            isSearchMode={true}
                        />
                    )}
                </AnimatePresence>
            </main>

            {/* WhatsApp Floating Action Button */}
            <WhatsAppFAB />

            <Footer onOpenLegal={handleOpenLegal} onSelectBrand={handleSelectBrand} />

            <SearchAssistant
                isOpen={isSearchAssistantOpen}
                onClose={() => setIsSearchAssistantOpen(false)}
                onSearch={handleSearch}
            />



            <InquiryDrawer
                currentBranch={currentBranch}
                onBranchChange={setCurrentBranch}
                onExplore={() => handleCategorySelect('catalogo')}
            />

            <LegalModal
                isOpen={legalModalOpen}
                type={legalModalType}
                onClose={() => setLegalModalOpen(false)}
            />

            {reviewTokenInfo && (
                <ReviewFormModal 
                    token={reviewTokenInfo.token}
                    onClose={() => {
                        setReviewTokenInfo(null);
                        const url = new URL(window.location.href);
                        url.searchParams.delete('review');
                        url.searchParams.delete('token');
                        window.history.pushState({}, '', url);
                    }}
                />
            )}
        </div>
    );
};

export default App;
